import { HubConnection } from '@microsoft/signalr';
import { DocType } from 'epic/common-form/Trade';
import type { Observable } from 'rxjs';
import type { SecondaryRealtimePrice } from 'state/secondaryMarket/realtime/realtime.model';
import { BcsProductKey } from 'state/workspaces/secondary-market/quotes/secondaryMarketQuotes.model';
import { PriceStreamingEvent } from 'utils/streaming/quotePriceEvent';
import { SmartQuoteEvent } from 'utils/streaming/smart-quote';

export type StreamingSource = 'SIGNALR' | 'POLLING';

export type NotificationEvent =
  | LightweightEvent
  | NewVersionEvent
  | SessionSynchronizationEvent
  | PriceImprovementEvent
  | SecondaryMarketEvent
  | SecondaryMarketPriceEvent
  | PriceStreamingEvent
  | DocumentationEvent
  | BackTestEvent
  | MailStatusEvent
  | SmartQuoteEvent;

export type NotificationEventType = NotificationEvent['type'];
export interface NewVersionEvent {
  eventId: string;
  type: NewVersionEventType;
  source: StreamingSource;
}
type NewVersionEventType = 'NewVersion';

export interface SessionSynchronizationEvent {
  eventId: string;
  type: SessionSynchronizationEventType;
  negotiationId: string;
  signalrConnectionId: string;
  source: StreamingSource;
}

export type SecondaryMarketPriceEvent = SecondaryRealtimePrice & {
  type: 'SecondaryMarketPrice';
  bcsProductKey: BcsProductKey;
};
export type SecondaryMarketEvent = SecondaryMarketLiveQuoteExpiryEvent;

export type MailStatusEvent = MailSummaryFailedEvent;

export type SecondaryMarketLiveQuoteExpiryEvent = {
  quoteId: string;
  type: 'SecondaryMarketLiveQuoteExpiry';
};
export type MailSummaryFailedEvent = {
  quoteId: string;
  orderId: string;
  type: 'MailSummaryFailed';
};

type NextDocumentationEvent = {
  type: 'NextDocumentation';
  quoteId: string;
  docType: DocType;
  language: string;
  url: string;
  public?: boolean;
  documentToken?: string;
};
export type DocumentationEvent =
  | NextDocumentationEvent
  | {
      type: 'NextDocumentationError';
      quoteId: string;
      docType: DocType;
      language: string;
      messages?: string[];
    };

export type PriceImprovementEvent =
  | PriceImprovementCreationEvent
  | PriceImprovementAcknowledgementEvent
  | PriceImprovementRejectionEvent
  | PriceImprovementValidationEvent;
export interface PriceImprovementCreationEvent {
  eventId: string;
  type: 'PriceImprovementCreated';
  comment: string;
  quoteId: string;
  sales: string;
  source: StreamingSource;
}
export interface PriceImprovementAcknowledgementEvent {
  eventId: string;
  type: 'PriceImprovementAcknowledged';
  rfpiId: string;
  quoteId: string;
  externalQuoteId: string;
  trader: string;
  source: StreamingSource;
}
export interface PriceImprovementRejectionEvent {
  eventId: string;
  type: 'PriceImprovementRejected';
  comment: string;
  rfpiId: string;
  quoteId: string;
  externalQuoteId: string;
  trader: string;
  source: StreamingSource;
}
export interface PriceImprovementValidationEvent {
  eventId: string;
  type: 'PriceImprovementValidated';
  comment: string;
  rfpiId: string;
  quoteId: string;
  externalQuoteId: string;
  trader: string;
  priceAdjustment: string;
  source: StreamingSource;
}

type SessionSynchronizationEventType =
  | 'SessionModified'
  | 'SessionDeleted'
  | 'SessionQuoting'
  | 'SessionQuoted'
  | 'SessionQuoteError'
  | 'SessionQuoteCancelled';

export type LightweightEventType =
  | 'NextBasketAdvisorResult'
  | 'NextBasketOptimizerResult'
  | 'NewBatchQuoteResult'
  | 'QuoteResult' // Legacy
  | 'NextQuoteResult'
  | 'NextDynamicQuoteResult'
  | 'DynamicBookingResult'
  | 'NextCostsAndCharges'
  | 'NextKidDoc'
  | 'NextPgDocAss'
  | 'NextPgGovDoc'
  | 'NextTradeAuth'
  | 'TradeExecutionReport' // Legacy
  | 'NextTradeExec'
  | 'ProductCorrelationData' // Legacy
  | 'NextAddonQuoteResult'
  | 'NextProductModificationResult'
  | 'AddonManagementQuoteResult'
  | 'OrdersBasketQuoteResult'
  | 'OrdersBasketExecutionResult';

export interface LightweightEvent {
  eventId: string;
  quoteId: Nullable<string>;
  reconciliationId?: string;
  uri: string;
  type: LightweightEventType;
  source: StreamingSource;
}

export interface Streaming {
  lightEvent$: Observable<NotificationEvent>;
  sessionSynchronizationMessage$: Observable<SessionSynchronizationEvent>;
  priceImprovementMessage$: Observable<PriceImprovementEvent>;
  secondaryMarketMessage$: Observable<SecondaryMarketEvent>;
  secondaryMarketPriceMessage$: Observable<SecondaryMarketPriceEvent>;
  backTestMessage$: Observable<BackTestEvent>;
  documentationMessage$: Observable<DocumentationEvent>;
  connectionEvent$: Observable<ConnectionEvent>;
  priceStreamingMessage$: Observable<PriceStreamingEvent>;
  mailStatusMessage$: Observable<MailStatusEvent>;
  smartQuoteMessage$: Observable<SmartQuoteEvent>;
  connection?: HubConnection;
}

export interface IFatEventApi {
  getFatEvent<T>(lightEvent: LightweightEvent, useAdvisory?: boolean): Observable<T>;
  getFatEventWithTabId<T>(lightEvent: LightweightEvent, tabId: string): Observable<T>;
}

export interface DisconnectedEvent {
  event: 'DISCONNECTED';
  error: Error;
}

export interface ReconnectingEvent {
  event: 'RECONNECTING';
  error: Error;
}

export interface ReconnectedEvent {
  event: 'RECONNECTED';
  connectionId: string;
}

export interface ConnectionSucceedEvent {
  event: 'CONNECTION_SUCCEED';
  connectionId: string;
}

export interface ConnectionFailedEvent {
  event: 'CONNECTION_FAILED';
  error: Error;
}

export type ConnectionEvent =
  | DisconnectedEvent
  | ReconnectingEvent
  | ReconnectedEvent
  | ConnectionSucceedEvent
  | ConnectionFailedEvent;

export function isConnectionEvent(val: unknown): val is ConnectionEvent {
  const event = (val as ConnectionEvent)?.event;
  const events: ConnectionEvent['event'][] = [
    'DISCONNECTED',
    'RECONNECTING',
    'CONNECTION_FAILED',
    'CONNECTION_SUCCEED',
    'RECONNECTED',
  ];
  return events.includes(event);
}

export type BackTestEvent = BackTestResultReadyEvent;
export type BackTestEventTypes = BackTestEvent['type'];
export type BackTestResultReadyEvent = { reconciliationId: string; type: 'BackTestResult' };
